import { type JSX } from 'react';
import { useGate, useUnit } from 'effector-react';
import {
  Autocomplete,
  FormControl,
  FormHelperText,
  FormLabel,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Listbox } from '../../components/listbox';
import { withControl } from '../../hoc/withControl';
import { ResidentsListControlGate, residentsListQuery } from './model';
import type { Resident, ResidentData } from './types';

export const ResidentsListControl = withControl<ResidentData>(
  ({
    value,
    setValue,
    error,
    label,
    required,
    readOnly,
    disabled,
    isError,
    residentType = [],
    withArchived = false,
  }) => {
    useGate(ResidentsListControlGate, { residentType, withArchived });
    const { data, pending } = useUnit(residentsListQuery);

    const findedValue =
      value !== ''
        ? (data?.find(
            (item: Resident) =>
              item.id === (typeof value === 'object' ? value?.id : value)
          ) ?? null)
        : null;

    const handleChange = (_: unknown, newValue: any) => setValue(newValue || null);

    return (
      <FormControl required={required} disabled={disabled}>
        {label && (
          <FormLabel required={!readOnly && required} error={isError}>
            {label}
          </FormLabel>
        )}
        <Autocomplete
          loading={pending}
          value={findedValue}
          options={!data ? [] : data}
          onChange={handleChange}
          slotProps={{
            listbox: {
              component: Listbox,
            },
          }}
          renderInput={(params): JSX.Element => (
            <TextField
              {...params}
              slotProps={{
                input: {
                  ...params.InputProps,
                  readOnly,
                  error: isError,
                  endAdornment: readOnly ? null : (
                    <>
                      {pending && <CircularProgress size={20} color="inherit" />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
            />
          )}
          readOnly={readOnly}
          disabled={disabled}
        />
        {error && <FormHelperText error>{error}</FormHelperText>}
      </FormControl>
    );
  }
);
